import { computed, inject, Injectable, signal } from '@angular/core';
import { LpRidLocationStop, LpRidRewardScope, LpRidScrapbookReward } from '../../../typings/schema';
import { IRidPlakboekjeItem, IRidPlakboekjeRewardContent, LEERPLUS } from '../rid-game.utils';
import { RidHomeworkService } from './rid-homework.service';
import { RidScrapbookService } from './rid-scrapbook.service';

/** A reward stuck into the scrapbook, together with the reward itself. */
export interface RidPlacedReward {
  uid: string;
  week: number;
  zIndex: number;
  placed: IRidPlakboekjeRewardContent;
  reward: LpRidScrapbookReward;
}

/** A rolled reward that isn't placed in the scrapbook yet, with the stop it was rolled at. */
export interface RidRolledReward {
  reward: LpRidScrapbookReward;
  stopUid?: string;
}

@Injectable()
export class RidRewardService {
  private readonly $lpApiService = inject(LEERPLUS.apiToken);
  private readonly $ridHomeworkService = inject(RidHomeworkService);
  private readonly $ridScrapbookService = inject(RidScrapbookService);
  private readonly rolled = signal<RidRolledReward | undefined>(undefined);

  readonly currentRoll = this.rolled.asReadonly();
  readonly currentReward = computed(() => this.rolled()?.reward);

  /**
   * Rolls a reward for the current stop and keeps it until it's placed in the scrapbook. Always rolls a new one: whether the stop
   * should get a reward is up to the flow in Studio.
   */
  rollRandomReward(): LpRidScrapbookReward {
    const stop = this.$ridHomeworkService.currentStop();
    const pool = this.getRewardPool(stop);
    if (pool.length === 0) throw new Error(`No RidScrapbookReward instances found to roll from for stop '${stop?.name}'`);

    const reward = pool[Math.floor(Math.random() * pool.length)];
    this.rolled.set({ reward, stopUid: stop?.uid });
    return reward;
  }

  resolveReward(activityReward: LpRidScrapbookReward | undefined): LpRidScrapbookReward {
    const reward = activityReward ?? this.currentReward();
    if (!reward) throw new Error('No reward set on the activity, and none has been rolled');

    return reward;
  }

  /** Whether the stop's reward is in the scrapbook. */
  isStopRewardGranted(stop: LpRidLocationStop): boolean {
    return this.$ridScrapbookService.items().some((item) => item.content.type === 'REWARD' && item.content.stopUid === stop.uid);
  }

  /** The rewards among the given scrapbook items (skipping any whose reward no longer exists), in the same order. */
  placedRewardsIn(items: IRidPlakboekjeItem[]): RidPlacedReward[] {
    const rewards = this.$lpApiService.getInstancesByType('RidScrapbookReward');
    return items.flatMap((item) => {
      if (item.content.type !== 'REWARD') return [];

      const placed = item.content;
      const reward = rewards.find((r) => r.uid === placed.uid);
      return reward ? [{ uid: item.uid, week: item.week, zIndex: item.zIndex, placed, reward }] : [];
    });
  }

  /**
   * The rewards the player doesn't own yet of the smallest scope that has any for the stop: the stop itself, else its location, else its
   * province, else global. Once the player owns them all, it repeats the rewards of the smallest scope that has any.
   */
  private getRewardPool(stop: LpRidLocationStop | undefined): LpRidScrapbookReward[] {
    const location = stop && this.$lpApiService.getInstancesByType('RidLocation').find((l) => l.stops.some((s) => s.uid === stop.uid));
    const province =
      location && this.$lpApiService.getInstancesByType('RidProvince').find((p) => p.locations.some((l) => l.uid === location.uid));

    // smallest scope first
    const scopeMatchers: ((scope: LpRidRewardScope) => boolean)[] = [
      (scope) => scope.dataType === 'RidRewardScopeStop' && scope.stop.uid === stop?.uid,
      (scope) => scope.dataType === 'RidRewardScopeLocation' && scope.location.uid === location?.uid,
      (scope) => scope.dataType === 'RidRewardScopeProvince' && scope.province.uid === province?.uid,
      (scope) => scope.dataType === 'RidRewardScopeGlobal',
    ];
    const pools = scopeMatchers.map((matches) =>
      this.$lpApiService.getInstancesByType('RidScrapbookReward').filter((reward) => reward.scopes.some(matches)),
    );
    const owned = new Set(this.placedRewardsIn(this.$ridScrapbookService.items()).map((r) => r.reward.uid));
    const unownedPool = pools.map((pool) => pool.filter((reward) => !owned.has(reward.uid))).find((pool) => pool.length > 0);
    return unownedPool ?? pools.find((pool) => pool.length > 0) ?? [];
  }
}
